/* Farmscraft product page: gallery, lightbox, quantity, quote form, sticky add-to-cart */
(() => {
  if (customElements.get('fcp-product')) return;

  const lockScroll = (on) => document.documentElement.classList.toggle('fcp-lock', on);

  class FcpProduct extends HTMLElement {
    connectedCallback() {
      this.initGallery();
      this.initLightbox();
      this.initQuantity();
      this.initQuote();
      this.initSticky();
      this.initReadMore();
    }

    /* Mobile gallery counter ("2 / 4") */
    initGallery() {
      const track = this.querySelector('.fcp-gallery__track');
      const count = this.querySelector('.fcp-gallery__count');
      if (!track || !count) return;
      const total = track.children.length;
      const update = () => {
        const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
        count.textContent = `${Math.min(index + 1, total)} / ${total}`;
      };
      track.addEventListener('scroll', update, { passive: true });
      update();
    }

    /* Full-screen image viewer */
    initLightbox() {
      const dialog = this.querySelector('.fcp-lightbox');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const track = dialog.querySelector('.fcp-lightbox__track');
      const count = dialog.querySelector('.fcp-lightbox__count');
      const total = track.children.length;
      const current = () => Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      const setCount = (index) => { if (count) count.textContent = `${index + 1} / ${total}`; };
      const updateCount = () => setCount(current());
      const go = (index) => {
        const target = Math.max(0, Math.min(total - 1, index));
        track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
        setCount(target);
      };

      this.querySelectorAll('[data-lightbox-index]').forEach((slide) => {
        slide.addEventListener('click', () => {
          dialog.showModal();
          lockScroll(true);
          const index = Number(slide.dataset.lightboxIndex) || 0;
          dialog.querySelectorAll('img[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
          });
          // The dialog is laid out as soon as showModal() returns, so jump straight to the image.
          track.scrollLeft = index * track.clientWidth;
          setCount(index);
        });
      });
      track.addEventListener('scroll', updateCount, { passive: true });
      dialog.querySelector('[data-lb-close]')?.addEventListener('click', () => dialog.close());
      dialog.querySelector('[data-lb-prev]')?.addEventListener('click', () => go(current() - 1));
      dialog.querySelector('[data-lb-next]')?.addEventListener('click', () => go(current() + 1));
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') go(current() - 1);
        if (event.key === 'ArrowRight') go(current() + 1);
      });
      dialog.addEventListener('close', () => lockScroll(false));
    }

    /* Quantity stepper */
    initQuantity() {
      const input = this.querySelector('.fcp-qty__input');
      if (!input) return;
      this.querySelectorAll('[data-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const next = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.qtyStep));
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }

    /* Quote request form in a dialog */
    initQuote() {
      const dialog = this.querySelector('.fcp-quote');
      if (!dialog || typeof dialog.showModal !== 'function') return;
      const open = () => { dialog.showModal(); lockScroll(true); };
      document.querySelectorAll(`[data-fcp-quote="${this.id}"]`).forEach((button) => button.addEventListener('click', open));
      dialog.querySelector('[data-quote-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => lockScroll(false));
      if (dialog.hasAttribute('data-open-on-load')) open();
    }

    /* Mobile bar that appears once the main add-to-cart button scrolls away */
    initSticky() {
      const bar = document.querySelector(`[data-fcp-sticky="${this.id}"]`);
      const target = this.querySelector('.fcp-buy') || this.querySelector('.fcp-alt');
      if (!bar || !target || !('IntersectionObserver' in window)) return;
      const setVisible = (visible) => {
        bar.classList.toggle('is-visible', visible);
        bar.toggleAttribute('inert', !visible);
        bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      };
      new IntersectionObserver(([entry]) => {
        setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(target);
      bar.querySelector('[data-fcp-sticky-atc]')?.addEventListener('click', () => {
        const main = this.querySelector('.product-form__submit');
        if (main && !main.disabled) main.click();
      });
    }

    /* "Read more" in the summary opens the full description */
    initReadMore() {
      const link = this.querySelector('[data-fcp-readmore]');
      const details = this.querySelector('#' + (link && link.getAttribute('href').slice(1)));
      if (!link || !details) return;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        details.open = true;
        details.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  customElements.define('fcp-product', FcpProduct);
})();
